/**
 * Quality-measure labels.
 *
 * Resolves the active defect-rate label from the platform-wide
 * ``quality_measure_ppk`` flag carried on {@link MenuSettingsContext}.
 *
 * - ``false`` (default) → PPM (Parts Per Million) — divisor 1,000,000
 * - ``true``            → PPK (Parts Per Thousand) — divisor 1,000
 *
 * Example:
 * ```tsx
 * const label = useQualityMeasureLabel(); // 'PPM' or 'PPK'
 * return <CardTitle>{label} Settings</CardTitle>;
 * ```
 */
import { useMenuSettings } from '../contexts/MenuSettingsContext';

export type QualityMeasureShortLabel = 'PPM' | 'PPK';
export type QualityMeasureLongLabel = 'Parts Per Million' | 'Parts Per Thousand';
export type QualityMeasureDivisor = 1_000 | 1_000_000;

/**
 * Returns the short label for the active defect-rate measure.
 *
 * @returns 'PPM' when the platform runs in parts-per-million mode (default),
 *          'PPK' when in parts-per-thousand mode.
 */
export function useQualityMeasureLabel(): QualityMeasureShortLabel {
  const { qualityMeasurePpk } = useMenuSettings();
  return qualityMeasurePpk ? 'PPK' : 'PPM';
}

/**
 * Returns the long-form label for the active defect-rate measure.
 *
 * @returns 'Parts Per Million' (default) or 'Parts Per Thousand'.
 */
export function useQualityMeasureLongLabel(): QualityMeasureLongLabel {
  const { qualityMeasurePpk } = useMenuSettings();
  return qualityMeasurePpk ? 'Parts Per Thousand' : 'Parts Per Million';
}

/**
 * Returns the divisor used to turn a defect ratio into the configured
 * PPM/PPK scale. 1,000 for PPK, 1,000,000 for PPM.
 *
 * Use this anywhere the frontend reverses the stored ``ppm`` scale back
 * to a parts-rejected count so the math stays consistent with
 * ``backend/core/quality.py::get_quality_divisor``.
 */
export function useQualityMeasureDivisor(): QualityMeasureDivisor {
  const { qualityMeasurePpk } = useMenuSettings();
  return qualityMeasurePpk ? 1_000 : 1_000_000;
}

/**
 * Pure helper for tests and non-hook contexts. Prefer the hook variants
 * inside React render trees.
 */
export function qualityMeasureLabelFor(ppk: boolean): QualityMeasureShortLabel {
  return ppk ? 'PPK' : 'PPM';
}

/**
 * Pure helper (long form) for tests and non-hook contexts.
 */
export function qualityMeasureLongLabelFor(ppk: boolean): QualityMeasureLongLabel {
  return ppk ? 'Parts Per Thousand' : 'Parts Per Million';
}

/**
 * Pure helper returning the PPM/PPK divisor for tests and non-hook
 * contexts. Prefer {@link useQualityMeasureDivisor} inside React trees.
 */
export function qualityMeasureDivisorFor(ppk: boolean): QualityMeasureDivisor {
  return ppk ? 1_000 : 1_000_000;
}
